export const FREE_CREDIT_BLOCKED_COUNTRY_CODES = new Set([
  'AR',
  'BD',
  'BR',
  'BY',
  'CN',
  'CO',
  'DZ',
  'EG',
  'GH',
  'ID',
  'IN',
  'IQ',
  'IR',
  'KE',
  'KZ',
  'LK',
  'MA',
  'MX',
  'MY',
  'NG',
  'NP',
  'PH',
  'PK',
  'RU',
  'TH',
  'TR',
  'UA',
  'UZ',
  'VN',
  'ZA',
]);

export const GOOGLE_ADS_REGISTRATION_GIFT_COUNTRY_CODES = new Set(['IN', 'PK']);

export const AUTH_DEVICE_COOKIE_NAME = 'mk_grok_device_id';

export interface RegistrationGiftRiskContext {
  deviceId: string | null;
  ipAddress: string | null;
  ipPrefix: string | null;
  asn: number | null;
}

export type AuthRequestCountrySource =
  | 'cloudflare_cf'
  | 'cf_ipcountry'
  | 'vercel_header'
  | 'custom_header'
  | 'missing';

export type RegistrationGiftEligibilityDenialReason =
  | 'non_canonical_gmail'
  | 'blocked_country';

export function normalizeCountryCode(countryCode?: string | null) {
  const normalized = countryCode?.trim().toUpperCase();
  return normalized && /^[A-Z]{2}$/.test(normalized) ? normalized : null;
}

export function isFreeCreditBlockedCountry(countryCode?: string | null) {
  const normalized = normalizeCountryCode(countryCode);
  return normalized ? FREE_CREDIT_BLOCKED_COUNTRY_CODES.has(normalized) : false;
}

export function normalizeGmailAddress(email?: string | null) {
  const normalized = email?.trim().toLowerCase();
  if (!normalized) {
    return null;
  }

  const [localPart, domain] = normalized.split('@');
  if (!localPart || !domain) {
    return normalized;
  }

  const gmailDomain = domain === 'gmail.com' || domain === 'googlemail.com';
  if (!gmailDomain) {
    return normalized;
  }

  const canonicalLocalPart = localPart.split('+')[0]?.replaceAll('.', '');
  return `${canonicalLocalPart}@gmail.com`;
}

export function isGmailAddress(email?: string | null) {
  const normalized = normalizeGmailAddress(email);
  return normalized?.endsWith('@gmail.com') ?? false;
}

export function isCanonicalGmailAddress(email?: string | null) {
  const normalized = email?.trim().toLowerCase();
  return Boolean(
    normalized?.endsWith('@gmail.com') &&
      normalizeGmailAddress(normalized) === normalized
  );
}

export function shouldGrantRegistrationGift(params: {
  email?: string | null;
  countryCode?: string | null;
  hasGoogleAdsAttribution?: boolean;
}) {
  return evaluateRegistrationGiftEligibility(params).eligible;
}

export function evaluateRegistrationGiftEligibility(params: {
  email?: string | null;
  countryCode?: string | null;
  hasGoogleAdsAttribution?: boolean;
}): {
  adsCountryOverrideApplied: boolean;
  denialReason: RegistrationGiftEligibilityDenialReason | null;
  eligible: boolean;
} {
  const countryCode = normalizeCountryCode(params.countryCode);
  const adsCountryOverrideApplied = Boolean(
    params.hasGoogleAdsAttribution &&
      countryCode &&
      GOOGLE_ADS_REGISTRATION_GIFT_COUNTRY_CODES.has(countryCode)
  );
  if (!isCanonicalGmailAddress(params.email)) {
    return {
      adsCountryOverrideApplied,
      denialReason: 'non_canonical_gmail',
      eligible: false,
    };
  }
  if (isFreeCreditBlockedCountry(countryCode) && !adsCountryOverrideApplied) {
    return {
      adsCountryOverrideApplied,
      denialReason: 'blocked_country',
      eligible: false,
    };
  }
  return {
    adsCountryOverrideApplied,
    denialReason: null,
    eligible: true,
  };
}

export function isGoogleAdsRegistrationGiftCountry(
  countryCode?: string | null
) {
  const normalized = normalizeCountryCode(countryCode);
  return normalized
    ? GOOGLE_ADS_REGISTRATION_GIFT_COUNTRY_CODES.has(normalized)
    : false;
}

export function getRegistrationGiftClaimEmail(email?: string | null) {
  return isCanonicalGmailAddress(email) ? normalizeGmailAddress(email) : null;
}

export function getAuthRequestCountryCode(context: unknown) {
  return getAuthRequestCountry(context).countryCode;
}

export function getAuthRequestCountry(context: unknown): {
  countryCode: string | null;
  source: AuthRequestCountrySource;
} {
  const request = getContextValue<Request>(context, 'request');
  const headers =
    request?.headers ?? getContextValue<Headers>(context, 'headers') ?? null;
  const candidates: Array<{
    source: Exclude<AuthRequestCountrySource, 'missing'>;
    value: string | null | undefined;
  }> = [
    {
      source: 'cloudflare_cf',
      value: getCfValue<string>(request, 'country'),
    },
    {
      source: 'cf_ipcountry',
      value:
        headers?.get('cf-ipcountry') ?? headers?.get('CF-IPCountry') ?? null,
    },
    {
      source: 'vercel_header',
      value: headers?.get('x-vercel-ip-country'),
    },
    { source: 'custom_header', value: headers?.get('x-country-code') },
  ];
  for (const candidate of candidates) {
    const countryCode = normalizeCountryCode(candidate.value);
    if (countryCode) return { countryCode, source: candidate.source };
  }
  return { countryCode: null, source: 'missing' };
}

export function getAuthRequestCookieHeader(context: unknown) {
  const request = getContextValue<Request>(context, 'request');
  const headers =
    request?.headers ?? getContextValue<Headers>(context, 'headers') ?? null;
  return headers?.get('cookie') ?? null;
}

export function getRegistrationGiftRiskContext(
  context: unknown
): RegistrationGiftRiskContext {
  const request = getContextValue<Request>(context, 'request');
  const headers =
    request?.headers ?? getContextValue<Headers>(context, 'headers') ?? null;
  const ipAddress = normalizeIpAddress(
    getCfValue<string>(request, 'clientIp') ??
      headers?.get('cf-connecting-ip') ??
      headers?.get('CF-Connecting-IP') ??
      headers?.get('x-real-ip') ??
      getFirstForwardedIp(headers?.get('x-forwarded-for'))
  );

  return {
    deviceId: normalizeDeviceId(
      getCookieValue(headers?.get('cookie'), AUTH_DEVICE_COOKIE_NAME)
    ),
    ipAddress,
    ipPrefix: getIpPrefix(ipAddress),
    asn: normalizeAsn(
      getCfValue<number | string>(request, 'asn') ??
        headers?.get('cf-asn') ??
        headers?.get('CF-ASN') ??
        headers?.get('x-asn') ??
        headers?.get('x-vercel-ip-as-number')
    ),
  };
}

export function normalizeDeviceId(deviceId?: string | null) {
  const normalized = deviceId?.trim();
  return normalized && /^[A-Za-z0-9_-]{16,128}$/.test(normalized)
    ? normalized
    : null;
}

export function normalizeIpAddress(ipAddress?: string | null) {
  const normalized = ipAddress?.trim();
  if (!normalized) {
    return null;
  }

  const withoutPort = normalized.startsWith('[')
    ? normalized.slice(1).split(']')[0]
    : normalized.includes(':') && normalized.includes('.')
      ? normalized
      : normalized.split(':').length === 2
        ? normalized.split(':')[0]
        : normalized;
  return withoutPort || null;
}

export function getIpPrefix(ipAddress?: string | null) {
  const normalized = normalizeIpAddress(ipAddress);
  if (!normalized) {
    return null;
  }

  if (normalized.includes('.')) {
    const parts = normalized.split('.');
    return parts.length === 4 ? `${parts.slice(0, 3).join('.')}.0/24` : null;
  }

  if (normalized.includes(':')) {
    const expanded = expandIpv6(normalized);
    return expanded ? `${expanded.slice(0, 3).join(':')}::/48` : null;
  }

  return null;
}

export function normalizeAsn(asn?: string | number | null) {
  const value = typeof asn === 'number' ? asn : Number(asn);
  return Number.isInteger(value) && value > 0 ? value : null;
}

function getContextValue<T>(context: unknown, key: string): T | undefined {
  return typeof context === 'object' && context !== null && key in context
    ? ((context as Record<string, unknown>)[key] as T)
    : undefined;
}

function getCfValue<T>(request: Request | undefined, key: string) {
  const cf = (
    request as unknown as { cf?: Record<string, unknown> } | undefined
  )?.cf;
  return cf?.[key] as T | undefined;
}

function getFirstForwardedIp(header?: string | null) {
  return header?.split(',')[0]?.trim() || null;
}

function getCookieValue(cookieHeader: string | null | undefined, name: string) {
  const cookies = cookieHeader?.split(';') ?? [];
  for (const cookie of cookies) {
    const [rawKey, ...rawValue] = cookie.trim().split('=');
    if (rawKey === name) {
      return decodeURIComponent(rawValue.join('='));
    }
  }
  return null;
}

function expandIpv6(ipAddress: string) {
  const [head, tail] = ipAddress.toLowerCase().split('::');
  const headParts = head ? head.split(':') : [];
  const tailParts = tail ? tail.split(':') : [];
  const missing = 8 - headParts.length - tailParts.length;
  if (missing < 0 || (tail === undefined && headParts.length !== 8)) {
    return null;
  }

  const parts = [
    ...headParts,
    ...Array.from({ length: missing }, () => '0'),
    ...tailParts,
  ];
  if (
    parts.length !== 8 ||
    parts.some((part) => !/^[0-9a-f]{1,4}$/.test(part))
  ) {
    return null;
  }

  return parts.map((part) => part.padStart(4, '0'));
}
